import { useCallback, useContext } from 'react';
import dateUtils from '@bingads-webui-component-legacy/humanize/date';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { fetchConversionGoal, transformFetchedGoal } from '../../fetch/conversion';
import { fetchKeywords } from '../../fetch/keywords';
import { fetchUetTags, getFetchedTagCollections, getFetchedTagsWithGoalsIds } from '../../fetch/uet';
import { EDIT_INTENT } from '../constants/intents';
import { maxFetchedGoalsCount, maxFetchedKeywordsCount, maxFetchedTagsCount } from '../constants/maximums';
import { createMeasurementTypeEnum, saveExperiment, updateExperiment } from '../../fetch/experiment';
import { API_DATE_FORMAT } from '../date';
import { errorMessages } from '../errors';
import { getKeywords, getUETTags } from '../utils';
import { validateLiftExperiment } from '../validate';
import { ADD_KEYWORDS_BY_ENTRY } from './keyword/keyword-methods';

const onFetchKeywordsSuccess = ({ send }) => (keywords) => {
  const orderedFetchedKeywords = [];
  const fetchedKeywords = {};

  keywords.forEach((keyword) => {
    const fetchedKeyword = {
      id: keyword.Id,
      name: keyword.Text,
      status: keyword.Status,
      impressions: keyword.PerformanceMetrics && keyword.PerformanceMetrics.Impressions,
    };
    orderedFetchedKeywords.push(fetchedKeyword);
    fetchedKeywords[keyword.Id] = { ...fetchedKeyword };
  });

  const updatedWizardData = {
    fetchedKeywords,
    isFetchingKeywords: false,
    orderedFetchedKeywords,
  };

  if (orderedFetchedKeywords.length === 0) {
    updatedWizardData.keywordsMethod = ADD_KEYWORDS_BY_ENTRY;
  }

  send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, updatedWizardData);
};

const onFetchUetTagsFailure = ({ i18n, msg, send }) => () => {
  notificationApi.showWarning(i18n.getString(msg));
  send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isUetTagsFetched: true });
};

const onFetchUetTagsSuccess = ({ i18n, scope, send }) => (data) => {
  const { fetchedTags, fetchedTagsAsArray } = getFetchedTagCollections(data);
  const tagsWithGoalsIds = getFetchedTagsWithGoalsIds(fetchedTagsAsArray);

  if (tagsWithGoalsIds.length > 0) {
    // For any fetched tags that have associated conversion goals, we need to fetch those goals and add them to the
    // tag before setting the fetch results in state.
    const msg = _TL_('Failed to fetch conversion goals.');
    fetchConversionGoal({
      count: maxFetchedGoalsCount,
      scope,
      tagId: tagsWithGoalsIds,
    })
      .then(({ value }) => {
        value.forEach((goal) => {
          fetchedTags[goal.Tag.Id].goals.push(transformFetchedGoal(goal));
        });
        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
          fetchedUetTags: fetchedTags,
          isUetTagsFetched: true,
          orderedFetchedUetTags: fetchedTagsAsArray,
        });
      })
      .catch(onFetchUetTagsFailure({ i18n, msg, send }));
  } else {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      fetchedUetTags: fetchedTags,
      isUetTagsFetched: true,
      orderedFetchedUetTags: fetchedTagsAsArray,
    });
  }
};

export const useConfigureStudyRepository = () => {
  const { scope } = useContext(ScopeContext);
  const { state: wizardState, send } = useWizardService();
  const { wizardData } = wizardState.context;
  const isEdit = wizardData.intent === EDIT_INTENT;

  const i18n = useContext(I18nContext);

  const load = useCallback(() => new Promise((resolve) => {
    if (wizardData.isFetchingKeywords == null) {
      wizardData.isFetchingKeywords = true;
      fetchKeywords({
        count: maxFetchedKeywordsCount,
        scope,
      })
        .then(onFetchKeywordsSuccess({ send }))
        .catch((error) => {
          notificationApi.showWarning(i18n.getString(_TL_('Failed to fetch keywords: {{ responseText }}'), { responseText: error.responseText }));
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isFetchingKeywords: false });
        });
    }

    if (!wizardData.isUetTagsFetched) {
      const msg = _TL_('Failed to fetch UET tags.');
      fetchUetTags({
        count: maxFetchedTagsCount,
        onFailure: onFetchUetTagsFailure({ i18n, msg, send }),
        onSuccess: onFetchUetTagsSuccess({ i18n, scope, send }),
        scope,
      });
    }

    setTimeout(() => resolve({}), 1000);
  }), [i18n, scope, send, wizardData.isFetchingKeywords, wizardData.isUetTagsFetched]);

  const onFailure = useCallback((message) => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { apiSaveErrorMessage: message });
  }, [send]);

  const save = useCallback(async () => {
    if (!validateLiftExperiment(wizardData)) {
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { showError: true });
      const saveValidationError = new Error(i18n.getString(errorMessages.SAVE_VALIDATION_FAILED));
      return Promise.reject(saveValidationError);
    }

    const payload = {
      Campaigns: Object.keys(wizardData.selectedCampaignsById).map(idAsString => Number(idAsString)),
      Country: wizardData.country,
      Keywords: getKeywords(wizardData),
      MeasurementType: createMeasurementTypeEnum({
        useConversionLift: wizardData.useConversionLift,
        useSearchLift: wizardData.useSearchLift,
        useVisitLift: wizardData.useVisitLift,
      }),
      Name: wizardData.experimentName,
      StartDate: dateUtils.toString(wizardData.startDate, API_DATE_FORMAT),
      SearchCustomerId: wizardData.searchCid ? Number(wizardData.searchCid) : null,
      UETTags: getUETTags(wizardData),
    };

    let saveResult;
    if (isEdit) {
      payload.Id = scope.values.liftExperimentId;
      saveResult = await updateExperiment(scope.values, { payload, onSuccess: () => { }, onFailure });
    } else {
      saveResult = await saveExperiment(scope.values, { payload, onSuccess: () => { }, onFailure });
    }

    return saveResult.isAPISaveFailure ? Promise.reject(saveResult.error) : Promise.resolve({});
  }, [i18n, isEdit, onFailure, scope.values, send, wizardData]);

  return {
    load,
    save,
  };
};
